import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAuth, useSelectedDate, getWeeksInMonth } from "../App";
import { TrendingUp, Calendar, Users, Trophy, Medal, Award, Building2, ArrowUp, ArrowDown, Minus, Download, FileText, CalendarDays } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { saveAs } from "file-saver";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const MONTH_ABBREV = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Department configurations with unique colors
const DEPARTMENTS = {
  all: { label: "Teams", color: "#6366F1", bgColor: "bg-indigo-600", textColor: "text-indigo-400" },
  sales: { label: "Sales", color: "#6366F1", bgColor: "bg-indigo-600", textColor: "text-indigo-400" },
  csm: { label: "Customer Success", color: "#10B981", bgColor: "bg-emerald-600", textColor: "text-emerald-400" },
  dev: { label: "Development", color: "#8B5CF6", bgColor: "bg-purple-600", textColor: "text-purple-400" },
  support: { label: "Support", color: "#F59E0B", bgColor: "bg-amber-600", textColor: "text-amber-400" },
  marketing: { label: "Marketing", color: "#EC4899", bgColor: "bg-pink-600", textColor: "text-pink-400" },
};

const DEPT_METRICS = {
  sales: [
    { key: "team_generated", label: "Team Gen" },
    { key: "self_generated", label: "Self Gen" },
    { key: "demos_held", label: "Demos" },
    { key: "upgrades_closed", label: "Upgrades" },
    { key: "new_deals_closed", label: "Deals" },
    { key: "deals_revenue", label: "Revenue ($)", isRevenue: true },
  ],
  csm: [
    { key: "renewals", label: "Renewals" },
    { key: "upsells", label: "Upsells" },
    { key: "customer_calls", label: "Calls" },
    { key: "nps_score", label: "NPS" },
    { key: "churn_prevention", label: "Saved" },
    { key: "renewals_revenue", label: "Ren Rev ($)", isRevenue: true },
    { key: "upsells_revenue", label: "Ups Rev ($)", isRevenue: true },
  ],
  dev: [
    { key: "tickets_closed", label: "Tickets" },
    { key: "features_shipped", label: "Features" },
    { key: "bugs_fixed", label: "Bugs" },
    { key: "code_reviews", label: "Reviews" },
    { key: "deployments", label: "Deploys" },
  ],
  support: [
    { key: "tickets_resolved", label: "Resolved" },
    { key: "first_response_time", label: "Response" },
    { key: "customer_satisfaction", label: "CSAT" },
    { key: "escalations_handled", label: "Escalations" },
    { key: "knowledge_articles", label: "KB Articles" },
  ],
  marketing: [
    { key: "leads_generated", label: "Leads" },
    { key: "campaigns_launched", label: "Campaigns" },
    { key: "content_published", label: "Content" },
    { key: "social_engagement", label: "Social" },
    { key: "email_opens", label: "Opens" },
  ],
};

const Reports = () => {
  const { token, isAdmin } = useAuth();
  const { selectedMonth, setSelectedMonth, selectedYear, setSelectedYear, viewMode: sharedViewMode, setViewMode: setSharedViewMode, selectedWeek, setSelectedWeek } = useSelectedDate();
  const [salespeople, setSalespeople] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [selectedUser, setSelectedUser] = useState("all"); // "all" or specific user id
  const [reportViewMode, setReportViewMode] = useState("month"); // "week", "month", or "year" - for this page's special year view
  const [allUsersStats, setAllUsersStats] = useState([]);
  const [weeklyEntries, setWeeklyEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };
  const currentMonth = new Date().getMonth() + 1;
  
  // Get weeks for the selected month
  const weeksInMonth = getWeeksInMonth(selectedYear, selectedMonth);

  // Filter salespeople by department for user dropdown
  const filteredSalespeople = useMemo(() => {
    if (selectedDepartment === "all") return salespeople;
    return salespeople.filter(p => p.department === selectedDepartment);
  }, [salespeople, selectedDepartment]);

  // Week options now come from shared context (weeksInMonth)
  // For Reports page compatibility, also create a lookup for week dates
  const weekDateLookup = useMemo(() => {
    const lookup = {};
    weeksInMonth.forEach((week, index) => {
      lookup[index + 1] = week.value; // Map 1, 2, 3... to actual dates
    });
    return lookup;
  }, [weeksInMonth]);

  useEffect(() => {
    fetchSalespeople();
  }, []);

  useEffect(() => {
    if (salespeople.length > 0) {
      fetchAllUsersStats();
    }
  }, [selectedYear, selectedMonth, selectedWeek, reportViewMode, salespeople, selectedDepartment, selectedUser]);

  // Reset user selection when department changes
  useEffect(() => {
    setSelectedUser("all");
  }, [selectedDepartment]);

  const fetchSalespeople = async () => {
    try {
      const response = await axios.get(`${API}/salespeople`, { headers });
      setSalespeople(response.data);
    } catch (error) {
      console.error("Error fetching salespeople:", error);
    }
  };

  const fetchAllUsersStats = async () => {
    setLoading(true);
    try {
      let filteredPeople = salespeople;
      
      // Filter by department first
      if (selectedDepartment !== "all") {
        filteredPeople = salespeople.filter(p => p.department === selectedDepartment);
      }
      
      // Then filter by specific user if selected
      if (selectedUser !== "all") {
        filteredPeople = filteredPeople.filter(p => p.id === selectedUser);
      }
      
      if (reportViewMode === "week") {
        // Fetch weekly entries for the selected week (selectedWeek is already a date string from context)
        const entriesRes = await axios.get(`${API}/entries?year=${selectedYear}&month=${selectedMonth}`, { headers });
        const entries = entriesRes.data.filter(e => {
          if (selectedWeek) {
            return e.week_ending_date === selectedWeek;
          }
          return true;
        });
        
        // Group by salesperson
        const statsByPerson = {};
        filteredPeople.forEach(person => {
          const personEntries = entries.filter(e => e.salesperson_id === person.id);
          const totals = {};
          personEntries.forEach(entry => {
            Object.entries(entry.metrics || {}).forEach(([key, val]) => {
              totals[key] = (totals[key] || 0) + val;
            });
          });
          statsByPerson[person.id] = { salesperson: person, yearly_totals: totals, monthly_totals: {} };
        });
        setAllUsersStats(Object.values(statsByPerson));
        
      } else if (reportViewMode === "month") {
        // Fetch monthly stats
        const statsPromises = filteredPeople.map(person => 
          axios.get(`${API}/stats/monthly/${person.id}?year=${selectedYear}&month=${selectedMonth}`, { headers })
            .then(res => ({ 
              salesperson: person, 
              yearly_totals: res.data.totals || {}, 
              monthly_totals: { [selectedMonth]: res.data.totals || {} }
            }))
            .catch(() => ({ salesperson: person, yearly_totals: {}, monthly_totals: {} }))
        );
        const allStats = await Promise.all(statsPromises);
        setAllUsersStats(allStats);
        
      } else {
        // Year view - original behavior
        const statsPromises = filteredPeople.map(person => 
          axios.get(`${API}/stats/yearly/${person.id}?year=${selectedYear}`, { headers })
            .then(res => ({ ...res.data, salesperson: person }))
            .catch(() => ({ salesperson: person, yearly_totals: {}, monthly_totals: {} }))
        );
        const allStats = await Promise.all(statsPromises);
        setAllUsersStats(allStats);
      }
    } catch (error) {
      console.error("Error fetching all users stats:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get metrics for a specific department (filter revenue for non-admins)
  const getMetricsForDept = (dept) => {
    const metrics = DEPT_METRICS[dept] || DEPT_METRICS.sales;
    // Only show revenue metrics to admin users
    if (!isAdmin) {
      return metrics.filter(m => !m.isRevenue);
    }
    return metrics;
  };

  // Calculate rankings - each user shows their own department's KPIs
  const rankedUsers = useMemo(() => {
    return [...allUsersStats]
      .map(stats => {
        const dept = stats.salesperson?.department || 'sales';
        const deptMetrics = getMetricsForDept(dept);
        const total = deptMetrics.reduce((sum, m) => sum + (stats.yearly_totals?.[m.key] || 0), 0);
        return { ...stats, total, department: dept, metrics: deptMetrics };
      })
      .sort((a, b) => b.total - a.total);
  }, [allUsersStats]);

  // Month-over-month comparison data
  const momComparison = useMemo(() => {
    const comparison = [];
    for (let i = 1; i <= 12; i++) {
      const monthData = { month: MONTH_ABBREV[i - 1] };
      let monthTotal = 0;
      let prevMonthTotal = 0;
      
      allUsersStats.forEach(stats => {
        const dept = stats.salesperson?.department || 'sales';
        const deptMetrics = getMetricsForDept(dept);
        const mData = stats.monthly_totals?.[i] || {};
        const prevMData = stats.monthly_totals?.[i - 1] || {};
        deptMetrics.forEach(m => {
          monthTotal += mData[m.key] || 0;
          prevMonthTotal += prevMData[m.key] || 0;
        });
      });
      
      monthData.total = monthTotal;
      monthData.change = i > 1 ? monthTotal - prevMonthTotal : 0;
      monthData.changePercent = i > 1 && prevMonthTotal > 0 ? ((monthTotal - prevMonthTotal) / prevMonthTotal * 100).toFixed(1) : 0;
      comparison.push(monthData);
    }
    return comparison;
  }, [allUsersStats]);

  const getTrophyIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-slate-300" />;
    if (rank === 3) return <Award className="w-5 h-5 text-amber-600" />;
    return null;
  };

  const getChangeIcon = (change) => {
    if (change > 0) return <ArrowUp className="w-3 h-3 text-emerald-400" />;
    if (change < 0) return <ArrowDown className="w-3 h-3 text-red-400" />;
    return <Minus className="w-3 h-3 text-slate-500" />;
  };

  // Export to CSV
  const exportToCSV = () => {
    const deptLabel = DEPARTMENTS[selectedDepartment]?.label || "Teams";
    const rows = rankedUsers.map(stats => {
      const deptMetrics = stats.metrics;
      return [
        stats.salesperson?.name || "Unknown",
        DEPARTMENTS[stats.department]?.label || stats.department,
        ...deptMetrics.map(m => (stats.yearly_totals?.[m.key] || 0).toFixed(0)),
        stats.total.toFixed(0)
      ];
    });
    
    const csvHeaders = ["Name", "Department", "M1", "M2", "M3", "M4", "M5", "Total"];
    const csvContent = [csvHeaders.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `${deptLabel.replace(/\s+/g, "_")}_Report_${selectedYear}.csv`);
  };

  // Export to PDF
  const exportToPDF = () => {
    const deptLabel = DEPARTMENTS[selectedDepartment]?.label || "Teams";
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.setTextColor(40);
    doc.text(`${deptLabel} Performance Report`, 14, 22);
    
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Year: ${selectedYear}`, 14, 30);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 36);
    
    const headers = [["Rank", "Name", "Department", "KPIs", "Total"]];
    const data = rankedUsers.map((stats, idx) => {
      const kpis = stats.metrics.map(m => `${m.label}: ${(stats.yearly_totals?.[m.key] || 0).toFixed(0)}`).join(", ");
      return [
        idx < 3 ? ["🥇", "🥈", "🥉"][idx] : `#${idx + 1}`,
        stats.salesperson?.name || "Unknown",
        DEPARTMENTS[stats.department]?.label || stats.department,
        kpis,
        stats.total.toFixed(0)
      ];
    });
    
    doc.autoTable({
      head: headers,
      body: data,
      startY: 45,
      theme: "striped",
      headStyles: { fillColor: [99, 102, 241] },
      styles: { fontSize: 8 },
      columnStyles: { 3: { cellWidth: 60 } }
    });
    
    doc.save(`${deptLabel.replace(/\s+/g, "_")}_Report_${selectedYear}.pdf`);
  };

  const deptColor = DEPARTMENTS[selectedDepartment]?.color || "#6366F1";

  // Get period label based on view mode
  const getPeriodLabel = () => {
    if (reportViewMode === "week") {
      const weekInfo = weekOptions.find(w => w.value === selectedWeek);
      return weekInfo?.label || `Week ${selectedWeek}`;
    } else if (reportViewMode === "month") {
      return `${MONTHS[selectedMonth - 1]} ${selectedYear}`;
    }
    return `${selectedYear}`;
  };

  if (loading && allUsersStats.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="reports-page">
      {/* Header - Fixed layout to prevent jumping */}
      <div className="mb-4">
        {/* Title Row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" style={{ color: deptColor }} />
            <h1 className="text-lg font-bold text-slate-100">Reports</h1>
          </div>
          
          {/* Export Buttons */}
          <div className="flex gap-2">
            <button onClick={exportToCSV} className="h-8 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-md flex items-center gap-1" data-testid="export-csv">
              <Download size={12} /> CSV
            </button>
            <button onClick={exportToPDF} className="h-8 px-2 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center gap-1" data-testid="export-pdf">
              <FileText size={12} /> PDF
            </button>
          </div>
        </div>

        {/* Filters Row - Fixed height container */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Department Filter */}
          <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
            <SelectTrigger className="w-[110px] h-8 text-xs bg-slate-800 border-slate-700">
              <Building2 size={12} className="mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {Object.entries(DEPARTMENTS).map(([key, dept]) => (
                <SelectItem key={key} value={key} className="text-slate-200 text-xs">
                  {dept.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* User Filter */}
          <Select value={selectedUser} onValueChange={setSelectedUser}>
            <SelectTrigger className="w-[110px] h-8 text-xs bg-slate-800 border-slate-700">
              <Users size={12} className="mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700 max-h-[300px]">
              <SelectItem value="all" className="text-slate-200 text-xs">All Users</SelectItem>
              {filteredSalespeople.map(person => (
                <SelectItem key={person.id} value={person.id} className="text-slate-200 text-xs">
                  {person.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* View Mode Toggle */}
          <div className="flex bg-slate-800 rounded-md p-0.5 border border-slate-700">
            <button
              onClick={() => setReportViewMode("week")}
              className={`px-2 py-1 text-xs rounded transition-colors ${reportViewMode === "week" ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white"}`}
            >
              Week
            </button>
            <button
              onClick={() => setReportViewMode("month")}
              className={`px-2 py-1 text-xs rounded transition-colors ${reportViewMode === "month" ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white"}`}
            >
              Month
            </button>
            <button
              onClick={() => setReportViewMode("year")}
              className={`px-2 py-1 text-xs rounded transition-colors ${reportViewMode === "year" ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white"}`}
            >
              Year
            </button>
          </div>

          {/* Date Selectors - Always show all 3 with visibility control */}
          <div className="flex gap-2">
            {/* Year Selector - Always visible */}
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="h-8 w-[80px] px-2 text-xs bg-slate-800 border border-slate-700 rounded-md text-white"
            >
              {[2024, 2025, 2026, 2027].map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>

            {/* Month Selector - Always rendered but disabled/hidden when Year mode */}
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              disabled={reportViewMode === "year"}
              className={`h-8 w-[90px] px-2 text-xs border border-slate-700 rounded-md ${
                reportViewMode === "year" 
                  ? "bg-slate-900 text-slate-600 cursor-not-allowed opacity-50" 
                  : "bg-slate-800 text-white"
              }`}
            >
              {MONTHS.map((month, idx) => (
                <option key={month} value={idx + 1}>{month.slice(0, 3)}</option>
              ))}
            </select>

            {/* Week Selector - Always rendered but disabled/hidden when not Week mode */}
            <select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              disabled={reportViewMode !== "week"}
              className={`h-8 w-[140px] px-2 text-xs border border-slate-700 rounded-md ${
                reportViewMode !== "week" 
                  ? "bg-slate-900 text-slate-600 cursor-not-allowed opacity-50" 
                  : "bg-slate-800 text-white"
              }`}
            >
              {weeksInMonth.map((week, idx) => (
                <option key={week.value} value={week.value}>Wk {idx + 1} ({new Date(week.value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Team Rankings - Full Width with KPIs */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50 mb-4">
        <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <Users size={16} /> Team Rankings - {getPeriodLabel()}
        </h3>
        
        <div className="space-y-3">
          {rankedUsers.map((stats, idx) => {
            const dept = stats.department;
            const deptConfig = DEPARTMENTS[dept] || DEPARTMENTS.sales;
            const deptMetrics = stats.metrics;
            
            return (
              <div 
                key={stats.salesperson?.id || idx}
                className={`p-4 rounded-lg border transition-all ${
                  idx === 0 ? "bg-yellow-500/10 border-yellow-500/40" :
                  idx === 1 ? "bg-slate-400/10 border-slate-400/40" :
                  idx === 2 ? "bg-amber-600/10 border-amber-600/40" :
                  "bg-slate-900/30 border-slate-700/30"
                }`}
              >
                {/* User Header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center justify-center w-8">
                    {getTrophyIcon(idx + 1) || (
                      <span className="text-slate-500 font-bold text-sm">#{idx + 1}</span>
                    )}
                  </div>
                  
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold ${deptConfig.bgColor}`}>
                    {stats.salesperson?.name?.charAt(0)}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-white text-base">{stats.salesperson?.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${deptConfig.bgColor} text-white`}>
                        {deptConfig.label}
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${
                      idx === 0 ? "text-yellow-400" : 
                      idx === 1 ? "text-slate-300" : 
                      idx === 2 ? "text-amber-500" : 
                      "text-slate-200"
                    }`}>
                      {stats.total.toFixed(0)}
                    </div>
                    <div className="text-[10px] text-slate-500">Total Points</div>
                  </div>
                </div>
                
                {/* KPIs Grid */}
                <div className="grid grid-cols-5 gap-2">
                  {deptMetrics.map((metric) => {
                    const value = stats.yearly_totals?.[metric.key] || 0;
                    const displayValue = metric.isRevenue 
                      ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                      : value.toFixed(0);
                    return (
                      <div 
                        key={metric.key}
                        className="p-2 rounded bg-slate-800/50 border border-slate-700/30"
                      >
                        <div className="text-[10px] text-slate-400 truncate">{metric.label}</div>
                        <div className={`text-sm font-bold ${metric.isRevenue ? 'text-emerald-400' : ''}`} style={{ color: metric.isRevenue ? undefined : deptConfig.color }}>
                          {displayValue}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Month-over-Month Section */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50 mb-4">
        <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <TrendingUp size={16} /> Month-over-Month Performance
        </h3>
        
        {/* Chart */}
        <div className="h-[180px] mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={momComparison}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="month" stroke="#64748B" tick={{ fontSize: 11 }} />
              <YAxis stroke="#64748B" tick={{ fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ backgroundColor: "#1E293B", border: "1px solid #334155", borderRadius: "8px", fontSize: "12px" }}
                labelStyle={{ color: "#F1F5F9" }}
              />
              <Line type="monotone" dataKey="total" stroke={deptColor} strokeWidth={2} dot={{ fill: deptColor, r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {/* MoM Data Grid */}
        <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-2">
          {momComparison.map((m, idx) => (
            <div 
              key={m.month}
              className={`p-2 rounded-lg text-center ${
                idx + 1 === currentMonth ? "bg-indigo-500/20 border border-indigo-500/40" : "bg-slate-900/30 border border-slate-700/30"
              }`}
            >
              <div className="text-[10px] text-slate-400 font-medium">{m.month}</div>
              <div className="text-sm font-bold text-slate-200">{(m.total || 0).toFixed(0)}</div>
              {m.change !== 0 && (
                <div className={`flex items-center justify-center gap-0.5 text-[9px] ${
                  m.change > 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {getChangeIcon(m.change)}
                  {m.change > 0 ? '+' : ''}{m.changePercent}%
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Reports;
